@file:OptIn(ExperimentalUuidApi::class)

package ai.wagemix.data

import com.fsryan.foundation.data.FSSyncEvent
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * The stats, taxonomy, areas and industries fetches of the Wagemix API.
 *
 * One nested sealed triple per operation (sync data source playbook, Step 2). Every event carries
 * the operation's inputs and nothing it produced: outcomes are written to a local data source and
 * observed there. `Failure` adds the error. Constructors are public because the backend variant
 * module constructs these events (controlled-construction playbook: sync-event lifecycle variants).
 */
sealed interface WMStatsSyncEvent<out E: WMStatsSyncEvent<E>>: FSSyncEvent<E> {

    /** One stats fetch, keyed by [signature] (see [statsSignature]); the result lands in the global local context. */
    sealed interface Stats<out E: Stats<E>>: WMStatsSyncEvent<E> {
        val signature: String

        data class Initial(
            override val signature: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): Stats<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val signature: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): Stats<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val signature: String,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): Stats<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** The occupation taxonomy, cached in [WMCache]. */
    sealed interface Taxonomy<out E: Taxonomy<E>>: WMStatsSyncEvent<E> {
        data class Initial(
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): Taxonomy<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): Taxonomy<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): Taxonomy<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** The areas that have data for [occupationIds], cached in [WMCache]. */
    sealed interface Areas<out E: Areas<E>>: WMStatsSyncEvent<E> {
        val occupationIds: Set<Int>

        data class Initial(
            override val occupationIds: Set<Int>,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): Areas<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val occupationIds: Set<Int>,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): Areas<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val occupationIds: Set<Int>,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): Areas<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** The industries that have data for [occupationIds], cached in [WMCache]. */
    sealed interface Industries<out E: Industries<E>>: WMStatsSyncEvent<E> {
        val occupationIds: Set<Int>

        data class Initial(
            override val occupationIds: Set<Int>,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): Industries<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val occupationIds: Set<Int>,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): Industries<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val occupationIds: Set<Int>,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): Industries<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }
}
